import { useState, useMemo } from 'react'
import { Download, Filter, ChevronDown, ChevronUp, Upload, Pencil, X, Check } from 'lucide-react'
import { formatNaira } from '../../utils/formatters'
import CreditBulkUploadModal from './CreditBulkUploadModal'

const STATUS_COLORS = {
  recorded: 'bg-blue-100 text-blue-700',
  batched: 'bg-amber-100 text-amber-700',
  exported: 'bg-purple-100 text-purple-700',
  reconciled: 'bg-green-100 text-green-700',
}

const PRODUCTS = ['AGO', 'PMS', 'DPK']

export default function CreditLedger({ currentUser, branches, companies, drivers, prices, sales, onBulkImport, onEdit }) {
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'superadmin'
  const canEdit = currentUser.role === 'admin' || currentUser.role === 'superadmin' || currentUser.role === 'manager'
  const [branchFilter, setBranchFilter] = useState(currentUser.branchId || 'all')
  const [companyFilter, setCompanyFilter] = useState('all')
  const [productFilter, setProductFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState({})
  const [editReason, setEditReason] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')

  function startEdit(sale) {
    setEditingId(sale.id)
    setEditDraft({ litres: sale.litres, destination: sale.destination, notes: sale.notes || '' })
    setEditReason('')
    setEditError('')
  }

  function cancelEdit() {
    setEditingId(null)
    setEditDraft({})
    setEditReason('')
    setEditError('')
  }

  async function saveEdit(sale) {
    if (!editReason.trim()) { setEditError('Please enter a reason for the edit.'); return }
    if (!editDraft.litres || Number(editDraft.litres) <= 0) { setEditError('Litres must be greater than 0.'); return }
    setEditSaving(true)
    setEditError('')
    const result = await onEdit(sale.id, {
      litres: Number(editDraft.litres),
      destination: editDraft.destination,
      notes: editDraft.notes,
      unitPriceAtSale: sale.unitPriceAtSale,
    }, editReason.trim())
    setEditSaving(false)
    if (result?.success === false) { setEditError(result.message || 'Failed to save.'); return }
    cancelEdit()
  }

  const visibleBranches = currentUser.role === 'manager'
    ? branches.filter((b) => b.id === currentUser.branchId)
    : branches

  const branchCompanies = useMemo(() => {
    if (branchFilter === 'all') return companies
    return companies.filter((c) => c.branchId === branchFilter)
  }, [companies, branchFilter])

  const filtered = useMemo(() => {
    return sales.filter((s) => {
      if (branchFilter !== 'all' && s.branchId !== branchFilter) return false
      if (companyFilter !== 'all' && s.transportCompanyId !== companyFilter) return false
      if (productFilter !== 'all' && s.productType !== productFilter) return false
      if (statusFilter !== 'all' && s.status !== statusFilter) return false
      if (dateFrom && new Date(s.saleTime) < new Date(dateFrom)) return false
      if (dateTo && new Date(s.saleTime) > new Date(dateTo + 'T23:59:59')) return false
      return true
    }).sort((a, b) => new Date(b.saleTime) - new Date(a.saleTime))
  }, [sales, branchFilter, companyFilter, productFilter, statusFilter, dateFrom, dateTo])

  const totals = useMemo(() => ({
    count: filtered.length,
    litres: filtered.reduce((s, r) => s + r.litres, 0),
    value: filtered.reduce((s, r) => s + r.totalValue, 0),
  }), [filtered])

  const byCompany = useMemo(() => {
    const map = {}
    filtered.forEach((s) => {
      if (!map[s.companyName]) map[s.companyName] = { litres: 0, value: 0, count: 0 }
      map[s.companyName].litres += s.litres
      map[s.companyName].value += s.totalValue
      map[s.companyName].count += 1
    })
    return Object.entries(map).sort((a, b) => b[1].value - a[1].value)
  }, [filtered])

  function exportCSV() {
    const headers = ['Receipt No', 'Date', 'Branch', 'Company', 'Driver', 'Plate', 'Destination', 'Product', 'Litres', 'Unit Price', 'Total Value', 'Status']
    const rows = filtered.map((s) => {
      const branch = branches.find((b) => b.id === s.branchId)
      const date = new Date(s.saleTime).toLocaleDateString('en-NG')
      return [
        s.receiptNumber, date, branch?.name || '', s.companyName, s.driverName,
        s.licencePlate || '', s.destination, s.productType,
        s.litres, s.unitPriceAtSale, s.totalValue, s.status,
      ].join(',')
    })
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `CreditSales_${dateFrom || 'all'}_${dateTo || 'all'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-800">Sales Ledger</h2>
          <p className="text-xs text-gray-400">{totals.count} sale{totals.count !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="p-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
          >
            <Filter className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center space-x-1.5 bg-blue-600 text-white text-sm font-semibold px-3 py-2 rounded-xl"
          >
            <Upload className="w-4 h-4" /><span>Import</span>
          </button>
          {filtered.length > 0 && (
            <button
              onClick={exportCSV}
              className="flex items-center space-x-1.5 bg-gray-800 text-white text-sm font-semibold px-3 py-2 rounded-xl"
            >
              <Download className="w-4 h-4" /><span>Export</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {currentUser.role !== 'manager' && (
              <select value={branchFilter} onChange={(e) => { setBranchFilter(e.target.value); setCompanyFilter('all') }}
                className="px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none col-span-2">
                <option value="all">All branches</option>
                {visibleBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
            <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="all">All companies</option>
              {branchCompanies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
            </select>
            <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="all">All products</option>
              {PRODUCTS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="all">All statuses</option>
              <option value="recorded">Recorded</option>
              <option value="batched">Batched</option>
              <option value="exported">Exported</option>
              <option value="reconciled">Reconciled</option>
            </select>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
        </div>
      )}

      {/* Totals summary */}
      {filtered.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <p className="text-xs text-gray-400">Sales</p>
            <p className="font-bold text-gray-800">{totals.count}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <p className="text-xs text-gray-400">Litres</p>
            <p className="font-bold text-gray-800">{totals.litres.toLocaleString('en-NG')}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <p className="text-xs text-gray-400">Total Value</p>
            <p className="font-bold text-gray-800 text-xs">{formatNaira(totals.value)}</p>
          </div>
        </div>
      )}

      {/* By-company breakdown */}
      {byCompany.length > 1 && (
        <div className="bg-white rounded-xl border border-gray-100 p-3 space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">By Company</p>
          {byCompany.map(([name, data]) => (
            <div key={name} className="flex justify-between items-center py-1 border-b border-gray-50 last:border-0">
              <span className="text-sm text-gray-700 truncate mr-2">{name}</span>
              <div className="text-right flex-shrink-0">
                <p className="text-xs font-bold text-gray-800">{formatNaira(data.value)}</p>
                <p className="text-xs text-gray-400">{data.litres.toLocaleString('en-NG')} L</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sales list */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <p className="text-sm text-gray-400">No sales found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((s) => {
            const date = new Date(s.saleTime)
            const dateStr = date.toLocaleDateString('en-NG', { day: '2-digit', month: 'short' })
            const timeStr = date.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' })
            const isExpanded = expandedId === s.id

            return (
              <div key={s.id} className="bg-white rounded-xl border border-gray-100">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : s.id)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="font-semibold text-sm text-gray-800 truncate">{s.companyName}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-600'}`}>
                        {s.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">{dateStr} {timeStr} · {s.productType} · {s.litres.toLocaleString('en-NG')} L</p>
                  </div>
                  <div className="ml-3 text-right flex-shrink-0">
                    <p className="text-sm font-bold text-gray-800">{formatNaira(s.totalValue)}</p>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 ml-auto mt-0.5" /> : <ChevronDown className="w-4 h-4 text-gray-400 ml-auto mt-0.5" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-3 border-t border-gray-50 pt-3 space-y-3">
                    {editingId === s.id ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="col-span-2 sm:col-span-1">
                            <label className="block text-xs text-gray-500 mb-1">Litres</label>
                            <input
                              type="number" min="0.01" step="0.01"
                              value={editDraft.litres}
                              onChange={(e) => setEditDraft((d) => ({ ...d, litres: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                          </div>
                          <div className="col-span-2 sm:col-span-1">
                            <label className="block text-xs text-gray-500 mb-1">Destination</label>
                            <input
                              type="text"
                              value={editDraft.destination}
                              onChange={(e) => setEditDraft((d) => ({ ...d, destination: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-500 mb-1">Notes</label>
                            <input
                              type="text"
                              value={editDraft.notes}
                              onChange={(e) => setEditDraft((d) => ({ ...d, notes: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-500 mb-1">Reason for edit <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              value={editReason}
                              onChange={(e) => setEditReason(e.target.value)}
                              placeholder="e.g. Corrected litres after recount"
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                          </div>
                        </div>
                        {editError && <p className="text-xs text-red-600">{editError}</p>}
                        <div className="flex space-x-2">
                          <button
                            onClick={() => saveEdit(s)}
                            disabled={editSaving}
                            className="flex-1 flex items-center justify-center space-x-1.5 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
                          >
                            <Check className="w-4 h-4" /><span>{editSaving ? 'Saving…' : 'Save'}</span>
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={editSaving}
                            className="flex-1 flex items-center justify-center space-x-1.5 py-2 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold disabled:opacity-50"
                          >
                            <X className="w-4 h-4" /><span>Cancel</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <span className="text-gray-400">Receipt</span><span className="font-mono text-gray-700">{s.receiptNumber}</span>
                          <span className="text-gray-400">Driver</span><span className="text-gray-700">{s.driverName}</span>
                          <span className="text-gray-400">Plate</span><span className="text-gray-700">{s.licencePlate || '—'}</span>
                          <span className="text-gray-400">Destination</span><span className="text-gray-700">{s.destination}</span>
                          <span className="text-gray-400">Unit Price</span><span className="text-gray-700">{formatNaira(s.unitPriceAtSale)}/L</span>
                          {s.notes && <><span className="text-gray-400">Notes</span><span className="text-gray-700">{s.notes}</span></>}
                          {s.editReason && <><span className="text-gray-400">Edit reason</span><span className="text-gray-700 italic">{s.editReason}</span></>}
                        </div>
                        {canEdit && onEdit && s.status === 'recorded' && (
                          <button
                            onClick={() => startEdit(s)}
                            className="flex items-center space-x-1.5 text-xs text-blue-600 font-medium hover:underline"
                          >
                            <Pencil className="w-3.5 h-3.5" /><span>Edit sale</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {showImport && (
        <CreditBulkUploadModal
          currentUser={currentUser}
          branches={branches}
          companies={companies}
          drivers={drivers}
          prices={prices}
          onImport={onBulkImport}
          onClose={() => setShowImport(false)}
        />
      )}
    </div>
  )
}
